// Angular
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
// Material
import { MatDialog } from '@angular/material/dialog';
// RxJS
import { BehaviorSubject, Observable, of } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { AppState } from '../../../../../../../core/reducers';
// CRUD
import { LayoutUtilsService, MessageType } from '../../../../../../../core/_base/crud';
// Services and Models
import {
  Affiliation,
  AffiliationOnServerCreated,
  AffiliationsService,
  selectAffiliationsActionLoading,
} from '../../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { selectErrorState } from 'src/app/core/lists/_selectors/affiliation.selectors';
import { AffiliationOnServerUpdated } from 'src/app/core/lists/_actions/affiliation.actions';
import { AffiliationType } from 'src/app/core/lists/_models/affiliation-type.model';
import { GeneralAutocompleteComponent } from '../../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { NgxPermissionsService } from 'ngx-permissions';
import { GeneralValidationMessagesService } from '../../../../../../../core/services/general-validation-messages.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-affiliation-edit',
  templateUrl: './affiliation-edit.component.html',
  styleUrls: ['affiliation-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AffiliationEditComponent implements OnInit, AfterViewInit, OnDestroy {
  affiliation: Affiliation;
  affiliationId$: Observable<number>;
  companyId: number;
  oldAffiliation: Affiliation;
  selectedTab = 0;
  loadingSubject = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean>;
  affiliationForm: FormGroup;
  hasFormErrors = false;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  descriptionTypes: AffiliationType[];
  urbanizationValue;
  Address1Value;
  Address2Value;
  cityValue;
  zipValue;
  params: any;
  getAffiliationTypeAutocomplete = this.affiliationService.searchAffiliationType.bind(
    this.affiliationService,
  );
  @ViewChild('affiliationTypeAutocomplete')
  affiliationTypeAutocomplete: GeneralAutocompleteComponent;
  view = false;
  tabs = [
    { index: 0, label: 'General Info', icon: 'mat-tab-label-icon fa fa-info', active: true },
    {
      index: 1,
      label: 'Relations',
      icon: 'mat-tab-label-icon fas fa-people-arrows',
      active: false,
      permission: 'canViewRelation',
    },
  ];
  loadingData = false;
  /**
   * Component constructor
   *
   * @param store: Store<AppState>
   * @param activatedRoute: ActivatedRoute
   * @param router: Router
   * @param affiliationFB: FormBuilder
   * @param affiliationService: AffiliationsService
   * @param translate: TranslateService,
   * @param cdr: ChangeDetectorRef
   * @param dialog: MatDialog,
   * @param permissionsService: NgxPermissionsService,
   * @param layoutUtilsService: LayoutUtilsService,
   */
  constructor(
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private affiliationFB: FormBuilder,
    private layoutUtilsService: LayoutUtilsService,
    private affiliationService: AffiliationsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private globalService: GeneralValidationMessagesService,
    private permissionsService: NgxPermissionsService,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
      params.tab_index ? this.changeTab(+params.tab_index) : this.changeTab(0);
    });
    this.affiliationService.getAllDescriptionTypes().subscribe((res) => {
      this.descriptionTypes = res[`data`];
    });

    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      if (!res) {
        this.router.navigateByUrl('/lists/affiliations');
      }
      this.companyId = res !== undefined ? res.companyID : 1;
    });
    this.subscriptions.push({ id: 1, sub: userSub });
    this.loading$ = this.loadingSubject.asObservable();
    this.loadingSubject.next(true);
    const routeSubscription = this.activatedRoute.params.subscribe((params) => {
      const id = params.id;
      if (id && id > 0) {
        const facilityById = this.globalService
          .getEntitiesById('affiliation_groups', id)
          .subscribe((result) => {
            if (result) {
              this.loadAffiliation(result.data);
              this.cdr.detectChanges();
              return;
            }
          });
        this.subscriptions.push({ sub: facilityById });
      } else {
        this.affiliation = new Affiliation();
        this.affiliation.clear();

        this.oldAffiliation = Object.assign({}, this.affiliation);
        this.initAffiliation();
      }
    });
    this.subscriptions.push({ id: 3, sub: routeSubscription });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.affiliation?.id) {
        this.affiliationTypeAutocomplete?.setValue({
          affiliation_type_id: this.affiliation.affiliation_type_id,
          affiliation_type_name: this.affiliation.affiliation_type_name,
          affiliation_type_description: this.affiliation.affiliation_type_description,
        });
        this.affiliationTypeAutocomplete?.updateValueAndValidity();
      }
    }, 1000);
  }

  loadAffiliation(_affiliation, fromService = false) {
    if (!_affiliation) {
      this.goBack();
      return;
    }
    this.affiliation = _affiliation;
    this.affiliationId$ = of(_affiliation.id);
    this.oldAffiliation = Object.assign({}, _affiliation);
    this.initAffiliation();
    if (fromService) {
      this.cdr.detectChanges();
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.cdr.detach();
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  /**
   * Init affiliation
   */
  initAffiliation() {
    this.createForm();
    this.loadingSubject.next(false);
  }

  /**
   * Create form
   */
  createForm() {
    this.affiliationForm = this.affiliationFB.group({
      office_number: [this.affiliation.office_number],
      extension: [this.affiliation.extension],
      name: [this.affiliation.name, Validators.required],
      description: [this.affiliation.description],
      email: [this.affiliation.email, Validators.compose([Validators.email, Validators.required])],
      urbanization: [this.affiliation.urbanization, Validators.maxLength(48)],
      city: [
        this.affiliation.city,
        Validators.compose([Validators.required, Validators.maxLength(30)]),
      ],
      state: [this.affiliation.state, Validators.required],
      zip: [
        this.affiliation.zip,
        Validators.compose([Validators.required, Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)]),
      ],
      address_1: [
        this.affiliation.address_1,
        Validators.compose([Validators.required, Validators.maxLength(48)]),
      ],
      address_2: [this.affiliation.address_2, Validators.maxLength(48)],
      affiliation_type: [null, Validators.required],
      note: [this.affiliation.note],
      phone: [
        this.affiliation.phone !== '' && this.affiliation.phone !== null
          ? '(' +
            this.affiliation.phone.substring(0, 3) +
            ') ' +
            this.affiliation.phone.substring(3, 6) +
            '-' +
            this.affiliation.phone.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      fax: [
        this.affiliation.fax !== '' && this.affiliation.fax !== null
          ? '(' +
            this.affiliation.fax.substring(0, 3) +
            ') ' +
            this.affiliation.fax.substring(3, 6) +
            '-' +
            this.affiliation.fax.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
    });
    this.urbanizationValue = this.affiliation.urbanization;
    this.Address1Value = this.affiliation.address_1;
    this.Address2Value = this.affiliation.address_2;
    this.cityValue = this.affiliation.city;
    this.zipValue = this.affiliation.zip;

    if (this.permissionsService.getPermission('canToggleSupervisingControl')) {
      this.affiliationForm.addControl(
        'supervising_control',
        new FormControl(this.affiliation.supervising_control || false),
      );
    }
    if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      this.affiliationForm.disable();
      this.view = true;
    }
    this.loadingData = true;
  }

  goBack() {
    this.router.navigateByUrl(localStorage.getItem('affiliationsListPath'));
    localStorage.removeItem('affiliationsListPath');
    this.loadingSubject.next(false);
  }

  reset() {
    this.affiliation = Object.assign({}, this.oldAffiliation);
    this.createForm();
    this.hasFormErrors = false;
    this.affiliationForm.markAsPristine();
    this.affiliationForm.markAsUntouched();
    this.affiliationForm.updateValueAndValidity();
  }

  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.affiliationForm.controls;
    /** check form */
    this.affiliationTypeAutocomplete.updateValueAndValidity();
    if (this.affiliationForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      this.selectedTab = 0;
      return;
    }

    const editedAffiliation = this.prepareAffiliation();

    if (editedAffiliation.id > 0) {
      this.updateAffiliation(editedAffiliation);
      return;
    }

    this.addAffiliation(editedAffiliation);
  }

  prepareAffiliation(): Affiliation {
    const controls = this.affiliationForm.controls;
    const _affiliation = new Affiliation();
    _affiliation.clear();
    _affiliation.address_1 = controls.address_1.value;
    _affiliation.address_2 = controls.address_2.value;
    _affiliation.id = this.affiliation.id;
    _affiliation.name = controls.name.value;
    _affiliation.email = controls.email.value;
    _affiliation.phone =
      controls.phone.value !== null
        ? controls.phone.value.replace(/\D/g, '')
        : controls.phone.value;
    _affiliation.zip = controls.zip.value;
    _affiliation.fax =
      controls.fax.value !== null ? controls.fax.value.replace(/\D/g, '') : controls.fax.value;
    _affiliation.note = controls.note.value;
    _affiliation.city = controls.city.value;
    _affiliation.state = controls.state.value.toUpperCase();
    _affiliation.description = controls.description.value;
    _affiliation.urbanization = controls.urbanization.value;
    _affiliation.affiliation_type_id = controls.affiliation_type.value?.affiliation_type_id;
    _affiliation.affiliation_type_name = controls.affiliation_type.value?.affiliation_type_name;
    _affiliation.affiliation_type_description =
      controls.affiliation_type.value?.affiliation_type_description;
    _affiliation.extension = controls.extension.value;
    _affiliation.office_number =
      controls.office_number.value != null
        ? controls.office_number.value.replace(/\D/g, '')
        : controls.office_number.value;

    if (this.permissionsService.getPermission('canToggleSupervisingControl')) {
      _affiliation.supervising_control = controls.supervising_control.value;
    }

    for (const [key, value] of Object.entries(_affiliation)) {
      if (typeof value === 'string') {
        _affiliation[key] = value.trim();
      }
    }
    return _affiliation;
  }

  /**
   * Add Affiliation
   *
   * @param _affiliation
   */
  addAffiliation(_affiliation: Affiliation) {
    this.loadingSubject.next(true);
    this.store.dispatch(new AffiliationOnServerCreated({ affiliation: _affiliation }));
    if (this.subscriptions.find((sub) => sub.id === 4) === undefined) {
      const actionLoadingSubscription = this.store
        .pipe(select(selectAffiliationsActionLoading))
        .subscribe((res) => {
          if (res !== true) {
            this.loadingSubject.next(false);
            if (this.subscriptions.find((sub) => sub.id === 5) === undefined) {
              const errorStateSubscription = this.store
                .pipe(select(selectErrorState))
                .subscribe((error) => {
                  if (error) {
                    if (error.error && error.error.errors) {
                      this.submissionErrorObj = error.error.errors;
                    } else {
                      this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                    }
                    this.hasSubmissionErrors = true;
                  } else if (this.router.routerState.snapshot.url !== '/lists/affiliations') {
                    this.submissionErrorObj = null;
                    this.hasSubmissionErrors = false;
                    const message = this.translate.instant('AFFILIATION.ADDED');
                    this.layoutUtilsService.showActionNotification(
                      message,
                      MessageType.Create,
                      3000,
                      false,
                      false,
                    );
                    this.goBack();
                  }
                });
              this.subscriptions.push({ id: 5, sub: errorStateSubscription });
            }
          }
          this.cdr.detectChanges();
        });
      this.subscriptions.push({ id: 4, sub: actionLoadingSubscription });
    }
  }

  /**
   * Update affiliation
   *
   * @param _affiliation
   */
  updateAffiliation(_affiliation: Affiliation) {
    this.loadingSubject.next(true);

    const updateAffiliation: Update<Affiliation> = {
      id: _affiliation.id,
      changes: _affiliation,
    };

    this.store.dispatch(
      new AffiliationOnServerUpdated({
        partialAffiliation: updateAffiliation,
        affiliation: _affiliation,
      }),
    );
    if (this.subscriptions.find((sub) => sub.id === 6) === undefined) {
      const actionLoadingSubscription = this.store
        .pipe(select(selectAffiliationsActionLoading))
        .subscribe((res) => {
          if (res !== true) {
            this.loadingSubject.next(false);
            if (this.subscriptions.find((sub) => sub.id === 7) === undefined) {
              const errorStateSubscription = this.store
                .pipe(select(selectErrorState))
                .subscribe((error) => {
                  if (error) {
                    if (error.error && error.error.errors) {
                      this.submissionErrorObj = error.error.errors;
                    } else {
                      this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                    }
                    this.hasSubmissionErrors = true;
                  } else if (this.router.routerState.snapshot.url !== '/lists/affiliations') {
                    this.goBack();
                    const message = this.translate.instant('AFFILIATION.UPDATED');
                    this.layoutUtilsService.showActionNotification(
                      message,
                      MessageType.Update,
                      3000,
                      false,
                      false,
                    );
                  }
                });
              this.subscriptions.push({ id: 7, sub: errorStateSubscription });
            }
          }
          this.cdr.detectChanges();
        });
      this.subscriptions.push({ id: 6, sub: actionLoadingSubscription });
    }
  }

  /**
   * Close Alert
   *
   */
  onAlertClose() {
    this.hasFormErrors = false;
  }

  /**
   * Checking control validation
   *
   * @param controlName
   * @param validationType
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.affiliationForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  getTitle() {
    let result = this.translate.instant('AFFILIATION.NEW_AFFILIATION');
    if (!this.affiliation || !this.affiliation.id) {
      return result;
    }

    result = `Affiliation - ${this.affiliation.name}`;
    return result;
  }

  putDash(e) {
    let x = e;
    if (x.length === 6 && x.charAt(5) !== '-') {
      x = x.slice(0, 5) + '-' + x.charAt(x.length - 1);
    }
    return x;
  }

  formatAffiliationTypeOption(type: AffiliationType): string {
    return type?.affiliation_type_name || '';
  }

  handleAffiliationTypeChange(type: AffiliationType): void {
    this.affiliationForm.get('affiliation_type').patchValue(type);
  }
  changeTab(index) {
    this.tabs = this.tabs.map((item) => {
      item.active = item.index === index;
      return item;
    });
    this.insertParam(index);
  }

  insertParam(value) {
    const queryParams = { tab_index: +value };
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
  editAffiliation(affiliation) {
    this.router.navigate(['lists/affiliations/edit/' + affiliation.id]);
  }
}
